import React, { useCallback, useEffect, useRef, useState } from 'react';

/**
 * Réplica em HTML do documento que o Prescreve entrega ao paciente.
 *
 * As coordenadas abaixo foram extraídas do content stream de um atestado real
 * e estão em pontos de PDF (A4 = 595,28 x 841,89). A folha é desenhada nesse
 * sistema e depois escalada para caber no container, então o resultado bate
 * com o documento oficial em qualquer largura de tela.
 *
 * O que difere do documento real, de propósito: o QR Code é inválido, não há
 * assinatura ICP-Brasil, o token/senha são placeholders e o rodapé traz a
 * observação de que é exemplo sem validade legal.
 */

const SHEET_W = 595.275591;
const SHEET_H = 841.889764;

// Baseline dentro da caixa de linha com line-height: 1, para Nunito Sans
// (ascent 1.011em, descent 0.353em): top = baseline - 0.829 * fontSize.
const BASELINE = 0.829;

const FONT_STACK = "'Nunito Sans', 'Segoe UI', Helvetica, Arial, sans-serif";

// Nunito Sans é a fonte do documento oficial. Carregada sob demanda para não
// entrar no caminho crítico do funil, que usa Poppins.
const FONT_HREF =
  'https://fonts.googleapis.com/css2?family=Nunito+Sans:wght@400;600;700&display=swap';

function ensureDocFontLoaded() {
  if (typeof document === 'undefined') return;
  if (document.querySelector('link[data-doc-preview-font]')) return;
  const link = document.createElement('link');
  link.rel = 'stylesheet';
  link.href = FONT_HREF;
  link.setAttribute('data-doc-preview-font', '1');
  document.head.appendChild(link);
}

const COLOR_TEXT = '#000000';
const COLOR_MUTED = '#4B5563';
const COLOR_SENHA = '#374151';
const COLOR_BOX_BORDER = '#D1D5DB';
const COLOR_DASH = '#9CA3AF';

export const SHEET_FOOTER_NOTE =
  'Exemplo de atestado — atestado sem validade legal.';

const TITLES = {
  atestado: 'ATESTADO',
  receita: 'RECEITA',
  exames: 'SOLICITAÇÃO DE EXAMES',
};

const LEGAL_TAIL =
  'Este documento foi emitido em conformidade com a Resolução CFM nº 2.314/2022, que regulamenta a telemedicina. '
  + 'Instruções para autenticação: acesse consultamais.app/autenticar e siga o guia. '
  + 'Contato: atendimento@consultamais.app | (41) 98843-0762';

function Line({ x, baseline, size, weight = 400, color = COLOR_TEXT, letterSpacing, children }) {
  return (
    <span
      style={{
        position: 'absolute',
        left: x,
        top: baseline - size * BASELINE,
        fontFamily: FONT_STACK,
        fontSize: size,
        fontWeight: weight,
        lineHeight: 1,
        color,
        whiteSpace: 'pre',
        letterSpacing,
      }}
    >
      {children}
    </span>
  );
}

/** Linha "Rótulo: valor" do cabeçalho, com o valor em posição absoluta. */
function Field({ baseline, label, valueX, value }) {
  return (
    <>
      <Line x={67.5} baseline={baseline} size={7.98} weight={700}>{label}</Line>
      <Line x={valueX} baseline={baseline} size={7.98}>{value}</Line>
    </>
  );
}

function buildBodyText({ docType, daysOff, startDate, cid, medications, exams }) {
  if (docType === 'receita') {
    const items = (medications && medications.length ? medications : ['Medicação conforme orientação médica'])
      .map((m, i) => `${i + 1} ${m}`)
      .join('. ');
    return `Uso oral — ${items}. ${LEGAL_TAIL}`;
  }
  if (docType === 'exames') {
    const items = (exams && exams.length ? exams : ['Exames conforme orientação médica']).join('; ');
    return `Solicito a realização dos seguintes exames complementares: ${items}. `
      + `${cid ? `Hipótese diagnóstica: ${cid} ` : ''}${LEGAL_TAIL}`;
  }
  const dias = daysOff ? `${daysOff} dia(s)` : 'os dias';
  const inicio = startDate ? ` a partir de ${startDate}` : '';
  const cidTrecho = cid
    ? `CID: ${cid}. O(a) paciente autoriza expressamente a inclusão do CID neste documento médico. `
    : '';
  return `Atesto, para os devidos fins, que o paciente, atendido por teleconsulta, necessita de ${dias} `
    + `de afastamento laboral${inicio}, em razão de doença. ${cidTrecho}${LEGAL_TAIL}`;
}

export default function DocPreviewSheet({
  docType = 'atestado',
  patientName = '',
  patientCpf = '',
  patientPhone = '',
  emittedAt = '',
  daysOff = null,
  startDate = '',
  cid = '',
  medications = null,
  exams = null,
  doctorName = 'Guilherme Esmanhotto',
  doctorCrmLine = 'Médico - CRM-DF nº: 23706',
  doctorSignLine = 'Guilherme Esmanhotto - Médico, CRM-DF 23706',
  address = 'Quadra CRS 502, Bloco C, Loja 37, Parte 4243, Asa Sul, Brasília/DF, CEP 70330-530',
  qrSrc = '/img/qr-exemplo-invalido.png',
  logoSrc = '/img/doc-header-consulta-mais.png',
}) {
  const wrapRef = useRef(null);
  const [scale, setScale] = useState(1);

  const measure = useCallback(() => {
    const el = wrapRef.current;
    if (!el) return;
    const w = el.clientWidth;
    if (w > 0) setScale(w / SHEET_W);
  }, []);

  useEffect(() => {
    ensureDocFontLoaded();
  }, []);

  useEffect(() => {
    measure();
    if (typeof ResizeObserver === 'undefined') {
      window.addEventListener('resize', measure);
      return () => window.removeEventListener('resize', measure);
    }
    const ro = new ResizeObserver(measure);
    if (wrapRef.current) ro.observe(wrapRef.current);
    return () => ro.disconnect();
  }, [measure]);

  const title = TITLES[docType] || TITLES.atestado;
  const body = buildBodyText({ docType, daysOff, startDate, cid, medications, exams });

  return (
    <div ref={wrapRef} style={{ width: '100%', height: SHEET_H * scale, overflow: 'hidden' }}>
      <div
        style={{
          position: 'relative',
          width: SHEET_W,
          height: SHEET_H,
          background: '#FFFFFF',
          transform: `scale(${scale})`,
          transformOrigin: 'top left',
        }}
      >
        <img
          src={logoSrc}
          alt="Consulta Mais"
          style={{ position: 'absolute', left: 56.25, top: 9.375, width: 482.775, height: 80.463 }}
        />

        <Field baseline={118.53} label="Emitente:" valueX={102.644} value={` ${doctorName}`} />
        <Line x={67.5} baseline={133.165} size={7.98}>{doctorCrmLine}</Line>
        <Field baseline={162.434} label="Paciente:" valueX={100.508} value={` ${patientName}`} />
        {/* Nestas duas linhas o offset original já embute o espaço após o rótulo. */}
        <Field baseline={177.068} label="CPF:" valueX={86.636} value={patientCpf} />
        <Field baseline={191.703} label="Telefone:" valueX={103.394} value={patientPhone} />
        <Field baseline={206.337} label="Data da emissão:" valueX={130.926} value={` ${emittedAt}`} />

        <div
          style={{
            position: 'absolute',
            left: 0,
            width: SHEET_W,
            top: 245.813 - 11.399 * BASELINE,
            textAlign: 'center',
            fontFamily: FONT_STACK,
            fontSize: 11.399,
            fontWeight: 700,
            lineHeight: 1,
            letterSpacing: 0.4664,
            color: COLOR_TEXT,
          }}
        >
          {title}
        </div>

        <div
          style={{
            position: 'absolute',
            left: 67.5,
            top: 272.135 - 8.5496 * BASELINE,
            width: 471.526,
            fontFamily: FONT_STACK,
            fontSize: 8.5496,
            fontWeight: 400,
            lineHeight: `${11.97}px`,
            color: COLOR_TEXT,
          }}
        >
          {body}
        </div>

        {/* Bloco de assinatura do documento oficial. */}
        <div
          style={{
            position: 'absolute',
            left: 70.46,
            top: 673.727,
            width: 454.355,
            height: 107.724,
            border: `0.75px solid ${COLOR_BOX_BORDER}`,
            borderRadius: 7.5,
            boxSizing: 'border-box',
          }}
        />
        <div
          style={{
            position: 'absolute',
            left: 78.71,
            top: 756.588,
            width: 437.855,
            height: 0,
            borderTop: `0.75px dashed ${COLOR_DASH}`,
          }}
        />
        <img
          src={qrSrc}
          alt="QR Code de exemplo, inválido"
          style={{ position: 'absolute', left: 82.46, top: 680.477, width: 57, height: 57 }}
        />

        <Line x={93.867} baseline={748.512} size={6} color={COLOR_SENHA}>Senha: </Line>
        <Line x={113.655} baseline={748.512} size={6} weight={600} color={COLOR_SENHA}>0000</Line>

        <Line x={149.21} baseline={689.989} size={7.98} weight={600}>Assinado digitalmente por</Line>
        <Line x={149.21} baseline={700.762} size={7.98}>{doctorSignLine}</Line>
        <Line x={149.21} baseline={713.035} size={7.98} weight={600}>Endereço:</Line>
        <Line x={149.21} baseline={723.808} size={7.98}>{address}</Line>

        <Line x={149.21} baseline={736.081} size={7.98}>Acesse a via digital deste documento em </Line>
        <Line x={296.786} baseline={736.081} size={7.98} weight={600}>https://app.prescreve.com/validar</Line>
        <Line x={415.99} baseline={736.081} size={7.98}> e informe o Token: </Line>
        <Line x={485.341} baseline={736.081} size={7.98} weight={600}>EXEMPLO</Line>

        <Line x={78.71} baseline={770.115} size={6.75}>Valide a assinatura ICP-Brasil em </Line>
        <Line x={180.967} baseline={770.115} size={6.75} weight={600}>https://validar.iti.gov.br</Line>
        <Line x={251.004} baseline={770.115} size={6.75}> escaneando o QR Code e informando a senha acima.</Line>

        <Line x={520.478} baseline={802.424} size={7.41} color={COLOR_MUTED}>1 / 1</Line>

        {/* A observação que o usuário pediu: pequena, no rodapé da folha. */}
        <Line x={70.46} baseline={793} size={6.4} weight={600} color="#B91C1C">
          {SHEET_FOOTER_NOTE}
        </Line>
      </div>
    </div>
  );
}
